import { useMemo } from "react";
import {
  PRIORITY_SCHEDULER_TAB_LABELS,
  VERTICAL_TABS_ALL_ITEM_VALUE,
  VERTICAL_TABS_BASE_GROUPS,
} from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { SchedulerViewType } from "../../../providers/CalendarProvider/CalendarProvider.types";
import { TabGroupItem } from "./CalendarVerticalTabs";

const baseTabGroups = {
  Unassigned: {
    label: PRIORITY_SCHEDULER_TAB_LABELS.UNASSIGNED,
    view: SchedulerViewType.Assignment,
    badgeTone: "unassigned" as const,
    disabledIfLocked: true,
  },
  "Not Ready": {
    label: PRIORITY_SCHEDULER_TAB_LABELS.NOT_READY,
    view: SchedulerViewType.Assignment,
    badgeTone: "notReady" as const,
    disabledIfLocked: true,
  },
  Assigned: {
    label: PRIORITY_SCHEDULER_TAB_LABELS.ASSIGNED,
    view: SchedulerViewType.Scheduler,
    badgeTone: "assigned" as const,
    disabledIfLocked: false,
  },
} as const satisfies Record<
  (typeof VERTICAL_TABS_BASE_GROUPS)[keyof typeof VERTICAL_TABS_BASE_GROUPS],
  {
    label: string;
    view: SchedulerViewType;
    badgeTone: "unassigned" | "notReady" | "assigned";
    disabledIfLocked: boolean;
  }
>;

export type TabGroupKey = keyof typeof baseTabGroups;

export const DEFAULT_GROUP = "Assigned" satisfies TabGroupKey;

type SchedulerTabGroups = Record<
  TabGroupKey,
  (typeof baseTabGroups)[TabGroupKey] & {
    items: TabGroupItem[];
    disabled: boolean;
  }
>;

function countWorkOrdersForTemplate(
  attempts: IAttemptListItemResponseForWorkOrder[],
  templateId: string
): number {
  return attempts.filter(
    (attempt) =>
      attempt.assignment?.workOrder?.workOrderTemplateId === templateId
  ).length;
}

function getTemplateCount(
  groupKey: TabGroupKey,
  templateId: string,
  rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[],
  rawNotReadyWorkOrders: IAttemptListItemResponseForWorkOrder[]
): number {
  if (groupKey === VERTICAL_TABS_BASE_GROUPS.UNASSIGNED) {
    return countWorkOrdersForTemplate(rawUnassignedWorkOrders, templateId);
  }
  if (groupKey === VERTICAL_TABS_BASE_GROUPS.NOT_READY) {
    return countWorkOrdersForTemplate(rawNotReadyWorkOrders, templateId);
  }
  return 0;
}

function buildGroupItems(
  groupKey: TabGroupKey,
  templates: ICalendarWorkTemplateBase[],
  rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[],
  rawNotReadyWorkOrders: IAttemptListItemResponseForWorkOrder[],
  doesCustomWorkOrderExist?: boolean
): TabGroupItem[] {
  const templateItems = templates
    .map((template) => ({
      disabled: false,
      hidden: template.isCustom && !doesCustomWorkOrderExist,
      label: template.name,
      value: template.id,
      count: getTemplateCount(
        groupKey,
        template.id,
        rawUnassignedWorkOrders,
        rawNotReadyWorkOrders
      ),
    }))
    .filter(({ hidden }) => !hidden);

  return [
    {
      disabled: false,
      isAll: true,
      label: PRIORITY_SCHEDULER_TAB_LABELS.ALL,
      value: VERTICAL_TABS_ALL_ITEM_VALUE,
      count: templateItems.reduce((sum, item) => sum + item.count, 0),
    },
    ...templateItems,
  ];
}

export function useNHCPrioritySchedulerTabs(
  workTemplateBases: ICalendarWorkTemplateBase[],
  events: IAttemptListItemResponseForWorkOrder[],
  rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[],
  rawNotReadyWorkOrders: IAttemptListItemResponseForWorkOrder[],
  doesCustomWorkOrderExist?: boolean
): SchedulerTabGroups {
  return useMemo(() => {
    const standardTemplateBases = workTemplateBases.filter(
      (template) => !template.isCustom
    );

    return Object.fromEntries(
      (Object.keys(baseTabGroups) as TabGroupKey[]).map((groupKey) => {
        return [
          groupKey,
          {
            ...baseTabGroups[groupKey],
            disabled: false,
            items: buildGroupItems(
              groupKey,
              groupKey === VERTICAL_TABS_BASE_GROUPS.ASSIGNED
                ? workTemplateBases
                : standardTemplateBases,
              rawUnassignedWorkOrders,
              rawNotReadyWorkOrders,
              doesCustomWorkOrderExist
            ),
          },
        ];
      })
    ) as SchedulerTabGroups;
  }, [
    workTemplateBases,
    events,
    rawUnassignedWorkOrders,
    doesCustomWorkOrderExist,
    rawNotReadyWorkOrders,
  ]);
}
